import { extendObservable, action, computed } from 'mobx';
import uniqBy from 'lodash/uniqBy';
import forEach from 'lodash/forEach';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import lodashCamelCase from 'lodash/camelCase';
import * as JsSearch from 'js-search';
import { debug } from 'util';
import { camelCase } from '@ridi/object-case-converter';
import axios, { setAxiosRootStoreAlertHandler } from '../../../libs/axios_csrf';
import LaneStore from './LaneStore';
import {
  BOARD_TYPES,
  CARD_RELATIONS,
  LANE_NAMES_TO_INTEGER
} from '../constants';
import AbstractRoot from '../../common_components/stores/AbstractRoot';
import AttachmentsStore from '../../common_components/attachments/stores/Attachments';
import GooglePicker from '../../../libs/google_picker';
import BoxPicker from '../../../libs/box_picker';
import Authorization from './AuthorizationStore';
import Card from './Card';

// useStrict(true);

function objectKeysToCamelCase(snake_case_object) {
  const camelCaseObject = {};

  forEach(snake_case_object, function(value, key) {
    if (isPlainObject(value) || isArray(value)) {
      // checks that a value is a plain object or an array - for recursive key conversion
      value = objectKeysToCamelCase(value); // recursively update keys of any values that are also objects
    }
    camelCaseObject[lodashCamelCase(key)] = value;
  });

  return camelCaseObject;
}

export default class RootStore extends AbstractRoot {
  constructor(
    {
      boardId,
      currentUserPermission,
      currentUserRoles,
      boardType,
      userLocale,
      isTaskPanel,
      isInclusiveBoard,
      vp_id,
      user_id,
      mvp_id,
      dashboardType,
      title,
      coreMembers,
      clearCurrentCoreMember
    },
    localeStore
  ) {
    super(
      (currentUserPermission && currentUserPermission.user) || {},
      currentUserRoles,
      localeStore
    );

    this.handleUpdateCard = this.handleUpdateCard.bind(this);
    this.handleCreationCard = this.handleCreationCard.bind(this);
    this.handleRemoveCard = this.handleRemoveCard.bind(this);

    this.authorizationStore = new Authorization(this, currentUserPermission);
    this.attachmentsStore = new AttachmentsStore(this);
    this.boardType = boardType;
    this.lanes = [];
    this.coreMembers = coreMembers || [];
    this.coFounders = [];
    this.ceo = {
      id: -1,
      name: '',
      profilePhotoUrl: ''
    };
    this.currentNote = null;
    this.boardMembers = [];
    this.allMembers = [];
    this.googlePicker = new GooglePicker();
    this.boxPicker = new BoxPicker();
    this.mvps = [];
    this.flattenMvps = [];
    this.mvpsSelectOptions = [];
    this.noteRelationSelectOptions = this.selectableNoteRelations;
    this.searchNotes = new JsSearch.Search('id');

    extendObservable(this, {
      clearCurrentCoreMember,
      dashboardType,
      vp_id,
      user_id,
      isInclusiveBoard,
      isTaskPanel,
      boardId,
      loading: true,
      windowsDimensions: {
        width: 0,
        height: 0
      },
      showAlert: false,
      completeness: {
        tasks: 0,
        ceo: 0
      },

      mvp_id,
      valueProposition: {},
      filterQuery: '',
      requestCount: 0,
      description: '...',
      name: '...',
      title: title || '...',

      subscriptionKeys: []
    });

    // this.fetchData();
    setAxiosRootStoreAlertHandler(this);

    const subscriptionData = this.isInclusiveBoard
      ? {
          dashboard_type: this.dashboardType,
          vp_id: this.vp_id,
          user_id: this.user_id,
          mvp_id: this.mvp_id
        }
      : { boardId: this.boardId };

    this.subscriptionKeys.push(
      createBoardSubscription(
        { ...subscriptionData, action: 'update' },
        this.handleUpdateCard
      )
    );

    this.subscriptionKeys.push(
      createBoardSubscription(
        { ...subscriptionData, action: 'create' },
        this.handleCreationCard
      )
    );

    this.subscriptionKeys.push(
      createBoardSubscription(
        { ...subscriptionData, action: 'destroy' },
        this.handleRemoveCard
      )
    );
  }

  @action async handleCreationCard({ card }) {
    let lane;

    if (this.isInclusiveBoard) lane = this.lanes[0];
    else lane = this.lanes.slice().find(e => e.id === card.list_id);

    lane.addNote(camelCase(card, true));
  }

  @action async handleUpdateCard({ card, list_name }) {
    let index;

    const lane = this.lanes.slice().find(e => {
      index = e.notes.slice().findIndex(c => c.id === card.id);
      return index != -1;
    });

    if (lane && index != -1) {
      lane.notes.remove(lane.notes[index]);
      lane._decrementNotesCounter();
    }

    let laneI;
    if (!this.isInclusiveBoard)
      laneI = this.lanes.slice().find(e => e.id === card.list_id);
    else laneI = this.lanes.slice().find(e => e.name === list_name);

    laneI.addNote(camelCase(card, true));
  }

  @action async handleRemoveCard({ card }) {
    let index;
    const lane = this.lanes.slice().find(e => {
      index = e.notes.slice().findIndex(c => c.id === card.id);
      return index != -1;
    });

    if (lane && index != -1) {
      lane._decrementNotesCounter();
      lane.notes.remove(lane.notes[index]);

      this.triggerChartUpdates();
    }
  }

  @action unsubscribeListener() {
    this.subscriptionKeys.forEach(removeBoardSubscription);
    this.subscriptionKeys = [];
  }

  @action setCurrentBoardId(id) {
    this.boardId = id;
    this.fetchData();
  }

  @action setCurrentUserPermission(currentUserPermission) {
    // Avoid recreating a store.Put the right logic inside the Authorization store
    // to update currentUserPermission and its dependencies.
    this.authorizationStore = new Authorization(this, currentUserPermission);
  }

  @action
  async fetchData() {
    try {
      let sortedBoardLists;

      if (this.isInclusiveBoard) {
        /**
        LaneStore consistency patch:

        As this BoardList is not defined in Backend,
        we must provide a way to display lanes. We create
        a serialized object here
        */
        const valueProposition = {
          bmcTasksBoard: {
            doingListId: 45,
            doneListId: 46,
            id: 14,
            toDoListId: 44
          },
          cardTodoLists: [],
          id: 1,
          labels: [],
          mvps: []
        };

        this.valueProposition = valueProposition;

        const response = await axios.get(`/execution/alltasks`, {
          params: {
            dashboard_type: this.dashboardType,
            vp_id: this.vp_id,
            user_id: this.user_id,
            mvp_id: this.mvp_id
          }
        });

        let totalCards = 0;
        sortedBoardLists = ['to_do', 'doing', 'done'].map((e, i) => {
          const cards = response.data[e].map(c => objectKeysToCamelCase(c));

          // / Monkey patch for data consistency issue
          // / Data provided by Rails serializer is an object
          cards.forEach((e, i) => {
            e.attachments = Object.values(e.attachments);
            e.cardsLabels = Object.values(e.cardsLabels);
            e.cardsMemberships = Object.values(e.cardsMemberships);
            e.comments = Object.values(e.comments);
            e.labels = Object.values(e.labels);
            e.sourceCardsRelations = Object.values(e.sourceCardsRelations);
            e.targetCardsRelations = Object.values(e.targetCardsRelations);
            e.teamAssignees = Object.values(e.cardsMemberships).map(
              member => member.user
            );
            e.todoList.items =
              e.todoList.items == undefined
                ? []
                : Object.values(e.todoList.items);
          });

          totalCards += cards.length;

          return {
            boardId: 14,
            cards,
            cardsCount: cards.length,
            description: null,
            id: -1,
            name: e,
            statistics: {
              createdCount: 4,
              effectiveness: 89,
              id: 1,
              movedInCount: 1,
              movedOutCount: 2,
              removedCount: 3
            }
          };
        });

        this.ceo = {};
        this.coFounders = [];
        this.completeness.tasks =
          (sortedBoardLists[2].cards.length * 100) / totalCards;

        this.accessMembers = this.coreMembers; // TODO Handle this
      } else {
        const response = await axios.get(
          `/igtasks/${this.boardId}/tasks.json`,
          {
            clearCacheEntry: true
          }
        );
        const { data } = response;

        this.valueProposition = data.valueProposition;

        this.valueProposition = data.valueProposition;
        this.description = data.description;
        this.name = data.name;
        this.title = data.name ? data.name : data.title;
        this.valueProposition = data.valueProposition;
        sortedBoardLists = this._sortBoardLists(data.lists);

        this.ceo = data.ceo;
        this.coFounders = data.coFounders;
        this.completeness.tasks = data.tasksCompleteness;
        this.completeness.ceo = data.ceoCompleteness;
        if (this.isTaskPanel) {
          this.authorizationStore.canShow = data.permission.can_show;
          this.authorizationStore.canShow = data.permission.can_update;
        }

        this.coreMembers = [];
        data.coreMembers.forEach(item => {
          this.coreMembers.push({
            id: item.id,
            name: item.name,
            profilePhotoUrl: item.profilePhotoUrl
          });
        });
        this.boardMembers = data.boardMembers;

        this.accessMembers = data.accessMembers;
      }

      if (this.lanes.length === 3) this.lanes = [];

      const responsible = this.boardMembers.find(
        member => member.id == this.user_id
      );

      if (responsible && this.clearCurrentCoreMember)
        this.clearCurrentCoreMember();

      if (this.user_id) {
        sortedBoardLists.map(
          list =>
            (list.cards = list.cards.filter(card => {
              return (
                (card.assignee ? card.assignee.id : null) == this.user_id ||
                card.cardsMemberships.find(
                  member => member.user_id == this.userId
                ) != undefined
              );
            }))
        );
      }

      sortedBoardLists.forEach(list => {
        this.lanes.push(new LaneStore(this, list));
      });

      this.allMembers = this._prepareAllMembers;

      this.mvps = this.valueProposition.mvps;
      this.mvpsSelectOptions = this.selectableMvpsBoards;
      this.initSearchNotes();
      this.loading = false;
    } catch (error) {
      if (error.status == 401) {
        this.authorizationStore.canShow = false;
        this.authorizationStore.canShow = false;
        this.loading = false;
      }

      console.log('Fetch: ', error.response || error);
    }
  }

  @computed get isVerySmallWindows() {
    return this.windowsDimensions.width < 700;
  }

  @computed get isSmallWindows() {
    return this.windowsDimensions.width < 992;
  }

  getLaneStoreByName(name) {
    return this.lanes.find(l => l.name === name);
  }

  getLaneStoreById(id) {
    return this.lanes.find(l => l.id === id);
  }

  @computed get isCeoBoard() {
    return this.boardType && this.boardType === BOARD_TYPES.CEO;
  }

  @computed get isTaskBoard() {
    return this.boardType && this.boardType === BOARD_TYPES.TASK;
  }

  _sortBoardLists(lists) {
    return lists.sort((a, b) => {
      return LANE_NAMES_TO_INTEGER[a.name] - LANE_NAMES_TO_INTEGER[b.name];
    });
  }

  findNoteById(noteId) {
    let note = null;

    this.lanes.some(l => {
      const result = l.notes.find(n => n.id === noteId);
      if (result) {
        note = result;
        return result;
      }
    });

    return note;
  }

  @action
  updateTasksChartAjax() {
    if (this.isInclusiveBoard) {
      let totalCards = 0;

      for (let i = 0; i < 3; i++) {
        totalCards += this.lanes[i].notesCount;
      }
      this.completeness.tasks = (this.lanes[2].notesCount * 100) / totalCards;
    } else {
      axios
        .get(`/igtasks/${this.boardId}/peformance.json`, {})
        .then(
          action(response => {
            // console.log(response);
            // updateTasksChart(response.data.completeness);
            this.completeness.tasks = response.data.completeness;
          })
        )
        .catch(
          action(error => {
            console.log(error);
          })
        );
    }
  }

  @action
  updateCeoChartAjax() {
    // console.log("updateCeoChartAjax");

    axios
      .get(`/igceo/${this.boardId}/ceo_perfomance.json`, {})
      .then(
        action(response => {
          // let board = JSON.parse(response.data.board);
          // updateCeoChart(board);
          this.completeness.ceo = response.data.ceoCompleteness;
          response.data.lists.forEach(
            list =>
              (this.getLaneStoreByName(list.name).statistics = list.statistics)
          );
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
  }

  @action
  triggerChartUpdates() {
    if (this.boardType === 'IgceoBoard') {
      this.updateCeoChartAjax();
    } else if (this.boardType === 'IgtasksBoard') {
      this.updateTasksChartAjax();
    }
  }

  @computed get _prepareAllMembers() {
    return uniqBy(this.coreMembers.concat(this.boardMembers), 'id');
  }

  @computed get allMembersForMentions() {
    return this.accessMembers.map(member => ({
      id: member.id,
      display: member.name
    }));
  }

  @computed get todoLaneStore() {
    return (
      this.getLaneStoreByName('to_do') || this.getLaneStoreByName('to_execute')
    );
  }

  // @computed get flattenMvpsBoards() {
  //
  //     if (!this.mvps) {
  //         return [];
  //     }
  //
  //     const mapped = this.mvps.map(mvp => {
  //         const {mvp_features, ...rest} = mvp;
  //         return [rest, ...mvp_features];
  //     });
  //
  //     return flatten(mapped)
  // }

  @computed get selectableMvpsBoards() {
    const mvpsOptions = this.mvps.map(mvp => ({
      label: mvp.name,
      disabled: mvp.board.id === this.boardId,
      options: [
        this._generateSelectableMvpElement(mvp),
        this._generateSelectableMvpFeatureElements(
          mvp.mvp_features,
          'features'
        ),
        this._generateSelectableMvpFeatureElements(
          mvp.cr_section_features,
          'customer_relation_features'
        ),
        this._generateSelectableMvpFeatureElements(
          mvp.infr_section_features,
          'infrastructure_features'
        ),
        this._generateSelectableMvpFeatureElements(
          mvp.mon_section_features,
          'monetization_features'
        )
      ]
    }));

    mvpsOptions.push(this._generateSelectableMvpBmcElement);

    return mvpsOptions;
  }

  _generateSelectableMvpElement(mvp) {
    return {
      label: mvp.name,
      value: mvp.board,
      disabled: mvp.board.id === this.boardId
    };
  }

  @computed get _generateSelectableMvpBmcElement() {
    return {
      label: 'BMC',
      value: this.valueProposition.bmcTasksBoard,
      disabled: this.valueProposition.bmcTasksBoard === this.boardId
    };
  }

  _generateSelectableMvpFeatureElements(features, name) {
    return {
      label: this.localeStore.intl.formatMessage({
        id: name
      }),
      options: features.map(feature =>
        this._generateSelectableMvpElement(feature)
      )
    };
  }

  @computed get selectableNoteRelations() {
    return Object.values(CARD_RELATIONS).map(relation => ({
      label: this.localeStore.intl.formatMessage({
        id: relation
      }),
      value: relation
    }));
  }

  @action
  allNotes() {
    if (this.notes === undefined) {
      this.notes = [];
      this.lanes.forEach(lane => {
        this.notes = this.notes.concat(lane.notes.toJS());
      });
    }
    return this.notes;
  }

  @action
  initSearchNotes() {
    this.searchNotes.addIndex('name');
    this.searchNotes.addIndex('description');
    this.searchNotes.addIndex(['creator', 'name']);
    this.searchNotes.addIndex('labelsNames');
    this.searchNotes.addIndex('todosDescriptions');
    this.searchNotes.addIndex('commentsBodies');

    this.searchNotes.addDocuments(this.allNotes());
  }
}
